import React from 'react';
import { Lifebuoy } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/** Gap kept from the viewport edge, and from the tools sidebar when it is open. */
export const FLOATING_HELP_EDGE_GAP_PX = 24;

/** Width the pill takes. Anything else docked in this corner should clear it. */
export const FLOATING_HELP_WIDTH_PX = 112;

const FLOATING_CONTAINER_CLASS =
    'fixed bottom-6 z-50 transition-[opacity,right,transform] duration-200 ease-out';

/**
 * The label is what tells people what the button is for, so it rides on the
 * button instead of hiding in a tooltip — and it stays there, whatever the
 * page is doing.
 */
const FLOATING_BUTTON_CLASS =
    'h-12 w-auto gap-0 rounded-full border border-border-strong bg-card pl-4 pr-5 text-sm font-semibold text-foreground shadow-elevation-2 ring-1 ring-border transition-[background-color,box-shadow,ring-color] duration-200 ease-out hover:bg-card hover:ring-border-strong motion-reduce:transition-none [&_svg]:size-5';

type HelpIcon = React.ComponentType<{ className?: string }>;

type HelpMenuAction = {
    id?: string;
    label: string;
    icon?: HelpIcon;
    onSelect?: () => void;
    href?: string;
    newTab?: boolean;
    /** Renders a divider above the item, to split learning from reference. */
    separatorBefore?: boolean;
};

type HelpMenuButtonProps = {
    actions: HelpMenuAction[];
    variant?: 'floating' | 'menu-item';
    label?: string;
    isVisible?: boolean;
    /** Width of the tools sidebar, so the button never sits on top of it. */
    rightOffset?: number;
    className?: string;
};

const openInNewTab = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
};

type FloatingHelpTriggerProps = {
    icon: HelpIcon;
    label: string;
    onClick?: () => void;
    asDropdownTrigger?: boolean;
};

const FloatingHelpTrigger: React.FC<FloatingHelpTriggerProps> = ({
    icon: Icon,
    label,
    onClick,
    asDropdownTrigger = false,
}) => {
    const button = (
        <Button variant="outline" className={FLOATING_BUTTON_CLASS} onClick={onClick}>
            <Icon />
            <span className="block whitespace-nowrap pl-2">{label}</span>
        </Button>
    );

    if (asDropdownTrigger) {
        return <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>;
    }

    return button;
};

type FloatingHelpContainerProps = {
    isVisible: boolean;
    rightOffset: number;
    className?: string;
    children: React.ReactNode;
};

const FloatingHelpContainer: React.FC<FloatingHelpContainerProps> = ({
    isVisible,
    rightOffset,
    className,
    children,
}) => (
    <div
        className={cn(
            FLOATING_CONTAINER_CLASS,
            isVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0',
            className,
        )}
        style={{ right: rightOffset + FLOATING_HELP_EDGE_GAP_PX }}
    >
        {children}
    </div>
);

export const HelpMenuButton: React.FC<HelpMenuButtonProps> = ({
    actions,
    variant = 'floating',
    label = 'Ajuda',
    isVisible = true,
    rightOffset = 0,
    className,
}) => {
    const handleAction = (action: HelpMenuAction) => {
        if (action.onSelect) {
            action.onSelect();
        }
        if (action.href) {
            if (action.newTab) {
                openInNewTab(action.href);
            } else {
                window.location.href = action.href;
            }
        }
    };

    const renderMenuItem = (action: HelpMenuAction) => {
        const ActionIcon = action.icon || Lifebuoy;

        return (
            <DropdownMenuItem onSelect={() => handleAction(action)} className="cursor-pointer">
                <ActionIcon className="mr-2 size-4" />
                {action.label}
            </DropdownMenuItem>
        );
    };

    const renderMenuItems = () =>
        actions.map((action) => (
            <React.Fragment key={action.id || action.label}>
                {action.separatorBefore ? <DropdownMenuSeparator /> : null}
                {renderMenuItem(action)}
            </React.Fragment>
        ));

    if (actions.length <= 1) {
        const action = actions[0];
        if (!action) return null;
        const ActionIcon = action.icon || Lifebuoy;

        if (variant === 'menu-item') {
            return renderMenuItem(action);
        }

        return (
            <FloatingHelpContainer
                isVisible={isVisible}
                rightOffset={rightOffset}
                className={className}
            >
                <FloatingHelpTrigger
                    icon={ActionIcon}
                    label={label}
                    onClick={() => handleAction(action)}
                />
            </FloatingHelpContainer>
        );
    }

    if (variant === 'menu-item') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <DropdownMenuItem className="cursor-pointer">
                        <Lifebuoy className="mr-2 size-4" />
                        {label}
                    </DropdownMenuItem>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" className="w-56">
                    {renderMenuItems()}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <FloatingHelpContainer
            isVisible={isVisible}
            rightOffset={rightOffset}
            className={className}
        >
            <DropdownMenu>
                <FloatingHelpTrigger icon={Lifebuoy} label={label} asDropdownTrigger />
                <DropdownMenuContent side="top" align="end" className="w-60">
                    {renderMenuItems()}
                </DropdownMenuContent>
            </DropdownMenu>
        </FloatingHelpContainer>
    );
};
